import {
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import { adminConfig, isEntraConfigured } from './config';

const apiScopes = [adminConfig.entra.apiScope];

export const msal = isEntraConfigured
  ? new PublicClientApplication({
      auth: {
        clientId: adminConfig.entra.clientId,
        authority: `https://login.microsoftonline.com/${adminConfig.entra.tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
    })
  : null;

export type AdminAccount = AccountInfo;

export async function initializeAuth(): Promise<AdminAccount | null> {
  if (!msal) {
    return null;
  }
  await msal.initialize();
  const result = await msal.handleRedirectPromise();
  const account = result?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  msal.setActiveAccount(account);
  return account;
}

export async function signIn(): Promise<void> {
  if (!msal) {
    throw new Error('Microsoft Entra sign-in is not configured.');
  }
  await msal.loginRedirect({ scopes: apiScopes, prompt: 'select_account' });
}

export async function signOut(): Promise<void> {
  if (!msal) {
    return;
  }
  await msal.logoutRedirect({ account: msal.getActiveAccount() });
}

export async function getAccessToken(): Promise<string> {
  const account = msal?.getActiveAccount();
  if (!msal || !account) {
    throw new Error('Sign in before calling the admin API.');
  }
  try {
    return (await msal.acquireTokenSilent({ scopes: apiScopes, account })).accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: apiScopes, account });
    }
    throw error;
  }
}
